<template>
    <div>
        <PageHeader>
            {{ $t('widgeterReportsShops.title') }}
            <template #side>
                <div class="d-flex">
                    <MultiSelectField
                        v-model="pages"
                        :lazy="true"
                        variant="select"
                        class="width-250 mb-0"
                        :options="pagesMultiselectFormat"
                        :placeholder="$t('yourPages')"
                        :loading="isSpacesLoading"
                        :disabled="isCampaignsLoading"
                        :show-checkboxes="true"
                    >
                        <template slot="selection" slot-scope="{ values, isOpen }">
                            <span v-if="values.length && !isOpen" class="multiselect__single">{{ $t('yourPages') }} ({{ values.length }})</span>
                        </template>
                    </MultiSelectField>

                    <div class="mx-2"></div>

                    <MultiSelectField
                        v-model="shops"
                        :lazy="true"
                        variant="select"
                        class="width-250 mb-0"
                        :options="shopsMultiselectFormat"
                        :placeholder="$t('shops')"
                        :loading="isCampaignsLoading"
                        :show-checkboxes="true"
                    >
                        <template slot="selection" slot-scope="{ values, isOpen }">
                            <span v-if="values.length && !isOpen" class="multiselect__single">{{ $t('shops') }} ({{ values.length }})</span>
                        </template>
                    </MultiSelectField>

                    <div class="mx-2"></div>

                    <TimeRangeCalendar v-model="timeRange" :disabled="isCampaignsLoading" />
                </div>
            </template>
        </PageHeader>

        <div class="row">
            <div class="col mb-4 overflow-hidden width-min-700 flex-grow-10">
                <MultiChartCard
                    :data="lineChartData"
                    :height="400"
                    :loading="isMultiChartCardLoading"
                    :options="lineChartOptions"
                    @currentChart="setCurrentChart"
                />
            </div>
            <div class="col mb-4 overflow-hidden width-400 width-min-400 flex-grow-1">
                <PieChart :chart-data="pieChartData[currentChart].series" :loading="isPieChartLoading" />
            </div>
        </div>

        <div class="row">
            <div class="col-xl-12 mb-4">
                <CampaignList :space-ids="pages || []" :campaign-ids="shops || []" :time-range="timeRange || {}" />
            </div>
        </div>
    </div>
</template>



<script>
    import { generateChartData, chartDataGroupBy } from '@/utils/chart';
    import PageHeader from '@/components/common/PageHeader.vue';
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import MultiSelectField from '@/components/common/MultiSelectField.vue';
    import MultiChartCard from '@/components/common/MultiChartCard.vue';
    import PieChart from './parts/PieChart.vue';
    import CampaignList from './parts/CampaignList.vue';

    export default {
        name: 'WidgeterReportsShops',
        components: {
            MultiSelectField,
            PageHeader,
            TimeRangeCalendar,
            MultiChartCard,
            PieChart,
            CampaignList,
        },
        data() {
            return {
                currentChart: 'offers_impressions',
                timeRange: null,
                pages: [],
                shops: [],
                isInit: false,
                lineChartData: this.getDefaultChartData(),
                pieChartData: this.getDefaultChartData(),
            };
        },
        computed: {
            isSpacesLoading() {
                return this.$store.state.space.loading.fetchSpaces;
            },
            isCampaignsLoading() {
                return this.isSpacesLoading || this.$store.getters['campaigns/getLoading']('campaigns');
            },
            isMultiChartCardLoading() {
                return this.isCampaignsLoading || this.$store.getters['userStats/getLoading']('campaignReport');
            },
            isPieChartLoading() {
                return this.isCampaignsLoading || this.$store.getters['userStats/getLoading']('campaignReport');
            },
            chartDataGroupBy() {
                return chartDataGroupBy(!this.timeRange);
            },
            lineChartOptions() {
                let options = {
                    tooltip: {
                        y: {
                            formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
                                if (this.currentChart === 'clicks' || this.currentChart === 'orders') return Math.round(value);
                                if (this.currentChart === 'amount' || this.currentChart === 'publisher_commission') return this.$format.currency(value);
                                return value;
                            },
                        },
                    },
                    yaxis: {
                        labels: {
                            formatter: (value, index) => {
                                if (this.currentChart === 'clicks' || this.currentChart === 'orders') return value.toFixed(0);
                                if (this.currentChart === 'amount' || this.currentChart === 'publisher_commission') return value.toFixed(2);
                                return value;
                            },
                        },
                    },
                };
                if (this.chartDataGroupBy === 'hour') {
                    options = {
                        ...options,
                        xaxis: {
                            labels: {
                                format: 'HH:mm',
                            },
                        },
                    };
                }
                return options;
            },
            chartDataFeature() {
                return this.currentChart;
            },
            campaignReport() {
                return this.$store.getters['userStats/getCampaignReport'];
            },
            spaces() {
                return this.$store.state.space.spaces;
            },
            campaigns() {
                return this.$store.getters['campaigns/getCampaigns']?.items || [];
            },
            pagesMultiselectFormat() {
                return Object.values(this.spaces).map(({ id, name }) => ({ value: id, text: name }));
            },
            shopsMultiselectFormat() {
                return this.campaigns.map(({ id, name }) => ({ value: id, text: name }));
            },
        },
        watch: {
            timeRange(newValue, oldValue) {
                if (oldValue !== null) {
                    this.fetchCampaignReport();
                }
            },
            currentChart() {
                if (!this.lineChartData[this.currentChart]?.series?.length || !this.pieChartData[this.currentChart]?.series?.length) {
                    this.fetchCampaignReport();
                }
            },
            campaignReport: {
                immediate: true,
                handler() {
                    this.updateChartData();
                },
            },
            pages() {
                if (this.isInit) this.fetchCampaigns();
            },
            shops() {
                this.fetchCampaignReport();
            },
            '$i18n.locale': 'updateChartNames',
        },
        methods: {
            updateChartNames() {
                const updatedNames = this.getDefaultChartData();
                Object.keys(this.lineChartData).forEach(key => {
                    if (this.lineChartData[key]) {
                        this.lineChartData[key].name = updatedNames[key].name;
                    }
                });
                Object.keys(this.pieChartData).forEach(key => {
                    if (this.pieChartData[key]) {
                        this.pieChartData[key].name = updatedNames[key].name;
                    }
                });
            },
            getDefaultChartData() {
                return {
                    offers_impressions: {
                        series: [],
                        name: this.$t('widgeterReportsShops.impressions'),
                    },
                    clicks: {
                        series: [],
                        name: this.$t('widgeterReportsShops.clicks'),
                    },
                    orders: {
                        series: [],
                        name: this.$t('widgeterReportsShops.transactions'),
                    },
                    amount: {
                        series: [],
                        name: this.$t('widgeterReportsShops.transactionAmount'),
                    },
                    publisher_commission: {
                        series: [],
                        name: this.$t('widgeterReportsShops.commission'),
                    },
                };
            },
            async init() {
                await this.fetchSpaces();
                this.selectAllPages();
                await this.fetchCampaigns();
                this.selectAllShops();
                this.isInit = true;
            },
            async fetchCampaignReport() {
                const requestParams = {
                    startDate: this.timeRange?.startDate,
                    endDate: this.timeRange?.endDate,
                    feature: this.chartDataFeature,
                    groupBy: this.chartDataGroupBy,
                    spaces: this.pages.join(','),
                    campaigns: this.shops.join(','),
                    showFirst: 9,
                };
                return this.$store.dispatch('userStats/fetchCampaignReport', { requestParams });
            },
            async fetchSpaces() {
                const requestParams = {
                    sort: 'name',
                    order: 'ASC',
                };
                return this.$store.dispatch('space/fetchSpaces', { requestParams });
            },
            async fetchCampaigns() {
                const requestParams = {
                    sort: 'name',
                    order: 'ASC',
                    active: true,
                    allowedFor: this.pages.join(','),
                };
                return this.$store.dispatch('campaigns/fetchCampaigns', { requestParams });
            },
            selectAllPages() {
                this.pages = Object.values(this.spaces).map(({ id }) => parseInt(id));
            },
            selectAllShops() {
                this.shops = this.campaigns.map(({ id }) => parseInt(id));
            },
            setCurrentChart(currentChart) {
                this.currentChart = currentChart;
            },
            setTimeRange(timeRange) {
                this.timeRange = timeRange;
            },
            updateChartData() {
                // Generowanie `lineChartData` na podstawie `campaignReport.chart`
                let newLineChartData = [];
                if (this.campaignReport?.chart?.length) {
                    const chartData = {};

                    this.campaignReport.chart.forEach(item => {
                        Object.keys(item).forEach(name => {
                            if (name !== 'date') {
                                if (!chartData[name]) chartData[name] = [];
                                chartData[name].push({ date: item.date, value: item[name] });
                            }
                        });
                    });

                    // Tworzenie nowego zestawu danych dla wykresu liniowego
                    newLineChartData = Object.keys(chartData).map(name => ({
                        name,
                        data: generateChartData(this.timeRange, chartData[name], this.chartDataGroupBy).map(item => [item.date, item.value]),
                    }));

                    // Sprawdzenie, czy wszystkie wartości są zerowe
                    const isAllZero = newLineChartData.every(series => series.data.every(([_, value]) => value === 0));
                    this.lineChartData[this.currentChart].series = isAllZero ? [] : newLineChartData;
                } else {
                    this.lineChartData[this.currentChart].series = [];
                }

                // Generowanie `pieChartData` na podstawie `campaignReport.pie_chart`
                let newPieChartData = [];
                if (this.campaignReport?.pie_chart?.length) {
                    newPieChartData = this.campaignReport.pie_chart.map(item => ({ name: item[0], series: item[1] }));

                    // Sprawdzenie, czy wszystkie wartości `series` są zerowe
                    const isAllZero = newPieChartData.every(item => item.series === 0);
                    this.pieChartData[this.currentChart].series = isAllZero ? [] : newPieChartData;
                } else {
                    this.pieChartData[this.currentChart].series = [];
                }
            },
            updateSelectedShopsIfPagesWasChanged() {
                if (this.shops.length && this.campaigns.length) {
                    const campaignIds = this.campaigns.map(({ id }) => id);
                    const isOldId = this.shops.some(id => !campaignIds.includes(id));
                    if (isOldId) this.shops = this.shops.filter(id => campaignIds.includes(id));
                }
            },
        },
        created() {
            this.init();
        },
    };
</script>
